import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const AVAILABLE_LANGUAGES = [
    { code: 'fr', labelKey: 'language.french', shortLabel: 'FR' },
    { code: 'ar', labelKey: 'language.arabic', shortLabel: 'AR' },
    { code: 'en', labelKey: 'language.english', shortLabel: 'EN' },
]

export default function LanguageSelector({ className = '', compact = true, inverted = false }) {
    const { i18n, t } = useTranslation()

    const currentLanguage = useMemo(() => {
        const lang = i18n.resolvedLanguage || i18n.language || 'ar'
        return String(lang).split('-')[0]
    }, [i18n.language, i18n.resolvedLanguage])

    const onChangeLanguage = (event) => {
        const nextLanguage = event.target.value
        i18n.changeLanguage(nextLanguage)
    }

    const selectClassName = compact
        ? inverted
            ? 'h-9 w-[86px] appearance-none rounded-full border border-white/70 bg-transparent pl-8 pr-7 text-[11px] font-extrabold uppercase tracking-wide text-white outline-none transition-all hover:border-white hover:bg-white/10 focus:border-white focus:ring-2 focus:ring-white/20'
            : 'h-9 w-[86px] appearance-none rounded-full border border-slate-300 bg-gradient-to-b from-white to-slate-50 pl-8 pr-7 text-[11px] font-extrabold uppercase tracking-wide text-slate-700 outline-none shadow-[0_1px_4px_rgba(0,0,0,0.05)] transition-all hover:border-slate-400 hover:from-white hover:to-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:shadow-[0_0_0_2px_rgba(59,130,246,0.12)]'
        : 'h-10 appearance-none rounded-xl border border-slate-300 bg-gradient-to-b from-white to-slate-50 pl-9 pr-8 text-sm font-bold text-slate-700 outline-none shadow-[0_1px_4px_rgba(0,0,0,0.05)] transition-all hover:border-slate-400 hover:from-white hover:to-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100'

    return (
        <label className={`inline-flex items-center gap-2 ${className}`}>
            {!compact ? <span className="text-sm font-semibold text-slate-700">{t('language.switcher_label')}</span> : null}

            <span className="relative inline-flex items-center">
                <span className={`pointer-events-none absolute left-2.5 ${inverted ? 'text-white' : 'text-slate-500'}`} aria-hidden="true">
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 2a10 10 0 100 20 10 10 0 000-20z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2 12h20" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 2c2.5 2.7 4 6.2 4 10s-1.5 7.3-4 10c-2.5-2.7-4-6.2-4-10s1.5-7.3 4-10z" />
                    </svg>
                </span>

                <select
                    value={currentLanguage}
                    onChange={onChangeLanguage}
                    className={selectClassName}
                    aria-label={t('language.switcher_label')}
                    title={t('language.switcher_label')}
                >
                    {AVAILABLE_LANGUAGES.map((lang) => (
                        <option key={lang.code} value={lang.code} className="bg-white text-slate-700 font-semibold">
                            {compact ? `${lang.shortLabel}` : t(lang.labelKey)}
                        </option>
                    ))}
                </select>

                <span className={`pointer-events-none absolute right-2.5 ${inverted ? 'text-white' : 'text-slate-400'}`} aria-hidden="true">
                    <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M5.25 7.5L10 12.25 14.75 7.5" />
                    </svg>
                </span>
            </span>
        </label>
    )
}
